import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { Card, Button } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import FeatherIcon from 'feather-icons-react';
import AddPhaseDialog from '../../components/production/AddPhaseDialog';
import FactoryScene, { BUILDING_VIEWS, INTERIOR_VIEWS } from './FactoryScene';
import useMachineData from './useMachineData';
import MachinePanel from './MachinePanel';
import {
  BobinGroupPanel,
  useBobinsState,
  hallForToken,
  hallForPhase
} from './ProductionBobins';

// ─── CAMERA PRESETS ────────────────────────────────────────

const PRESETS = {
  perspective: { pos: [-50, 60, -30], look: [3.5, 0, 40] },
  top:         { pos: [3.5, 150, 45], look: [3.5, 0, 45] },
  front:       { pos: [3.5, 30, -45], look: [3.5, 0, 45] },
  right:       { pos: [80, 30, 45],   look: [3.5, 0, 45] },
  gate:        { pos: [3.5, 6, -12],  look: [3.5, 4, 40] }
};

const PRESET_BUTTONS = [
  { key: 'perspective', label: 'Perspective', icon: 'eye' },
  { key: 'top', label: 'Top View', icon: 'maximize' },
  { key: 'front', label: 'Front', icon: 'log-in' },
  { key: 'right', label: 'Side', icon: 'sidebar' },
  { key: 'gate', label: 'Gate View', icon: 'home' }
];

// ─── BUILDING INFO ─────────────────────────────────────────

const BUILDING_INFO = {
  'hall-1': { name: 'Production Hall 1', dims: '82.7m x 24m', color: '#5B9BD5' },
  'hall-2': { name: 'Production Hall 2', dims: '38.5m x 11.5m', color: '#70AD47' },
  'hall-right': { name: 'Hall 3 + Hall 4', dims: '82.3m x 24m', color: '#ED7D31' },
  'deposit-1': { name: 'Deposit 1', dims: '33m x 37m', color: '#E67E22' },
  'deposit-2': { name: 'Deposit 2', dims: '32m x 13m', color: '#8E44AD' }
};

const SECTION_NAMES = {
  'hall-1': 'Hall 1',
  'hall-2': 'Hall 2',
  'entrance': 'Entrance',
  'hall-3': 'Hall 3',
  'hall-4': 'Hall 4',
  'deposit-1': 'Deposit 1',
  'deposit-2': 'Deposit 2'
};

const DEFAULT_SECTION = {
  'hall-1': 'hall-1',
  'hall-2': 'hall-2',
  'hall-right': 'entrance',
  'deposit-1': 'deposit-1',
  'deposit-2': 'deposit-2'
};

// ─── MAIN PAGE ─────────────────────────────────────────────

export default function Factory3DPage() {
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [viewMode, setViewMode] = useState('overview');
  const [interiorSection, setInteriorSection] = useState(null);
  const [cameraTarget, setCameraTarget] = useState(null);
  const [cameraTrigger, setCameraTrigger] = useState(0);
  const [activePreset, setActivePreset] = useState('perspective');
  const [openPanel, setOpenPanel] = useState(null);
  const [selectedBobinGroup, setSelectedBobinGroup] = useState(null);
  const [selectedYardItem, setSelectedYardItem] = useState(null);
  const [phaseTarget, setPhaseTarget] = useState(null);
  const [yardBanner, setYardBanner] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const focusStartRef = useRef(0);
  const machineData = useMachineData();
  const queryClient = useQueryClient();

  const toggleFullscreen = useCallback(() => {
    const el = document.getElementById('factory3d-container');
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  }, []);

  const flyTo = useCallback(target => {
    setCameraTarget(target);
    setCameraTrigger(t => t + 1);
  }, []);

  // ── Deep-link: focus a specific token from Project Bobin State ──
  // URL: /apps/project-management/factory-3d?token=<jobId>:<tokenId>&phase=<phase>
  const [searchParams] = useSearchParams();
  const tokenParam = searchParams.get('token');
  const phaseParam = searchParams.get('phase') || '';
  const sepIdx = (tokenParam || '').indexOf(':');
  const focusJobId = sepIdx >= 0 ? tokenParam.slice(0, sepIdx) : '';
  const focusTokenId = sepIdx >= 0 ? tokenParam.slice(sepIdx + 1) : '';
  const [focusToken, setFocusToken] = useState(null); // {jobId, tokenId}
  const [focusNote, setFocusNote] = useState(null);
  const { data: bobinsData } = useBobinsState();
  const focusHandledRef = useRef(false);
  const focusLocatedRef = useRef(false);

  // Once the floor snapshot is loaded, jump into the hall the token lives in and
  // arm the beacon. If the token isn't physically on the floor (planned / consumed
  // / packed), fall back to the hall its phase belongs to and show a note.
  useEffect(() => {
    if (!tokenParam || focusHandledRef.current) return;
    if (!bobinsData) return; // wait for /physical-tokens
    const tokens = bobinsData.tokens || [];
    const found = tokens.find(
      t =>
        String(t.jobId) === String(focusJobId) &&
        String(t.tokenId) === String(focusTokenId)
    );
    const hall = found ? hallForToken(found) : hallForPhase(phaseParam);
    const building = hall === 'hall-1' ? 'hall-1' : 'hall-right';

    focusHandledRef.current = true;
    setSelectedBuilding(building);
    setViewMode('interior');
    setInteriorSection(hall);
    setActivePreset(null);
    flyTo(INTERIOR_VIEWS[hall]);
    setFocusToken({ jobId: focusJobId, tokenId: focusTokenId });

    if (!found) {
      const hallName = hall.replace('hall-', 'Hall ');
      setFocusNote(
        `Token ${focusTokenId?.slice(0, 16)}… nu e fizic pe podea acum — te-am dus în ${hallName}${
          phaseParam ? ` (faza ${phaseParam})` : ''
        }.`
      );
    }
  }, [tokenParam, bobinsData, focusJobId, focusTokenId, phaseParam, flyTo]);

  // Whether the bobin panel should open straight into the planning step (only when
  // reached via a deep link, not a normal group click).
  const [bobinAutoPlan, setBobinAutoPlan] = useState(false);

  // Normal group click → open at the tokens step.
  const handleSelectBobinGroup = useCallback(g => {
    setBobinAutoPlan(false);
    setSelectedBobinGroup(g);
  }, []);

  // A drum standing in the yard: finished cable, waiting for the warehouse to
  // take it. Clicking it opens the same Add-phase dialog the Production →
  // Warehouse table has — the drum can be sent back into production from here.
  const handleSelectYardItem = useCallback(item => {
    setSelectedYardItem(item);
  }, []);

  // The dialog releases the StorageItem, so the drum has to leave the yard on
  // screen too — both the 3D yard and the queue read it from the warehouse.
  const handleYardPhaseAdded = useCallback(
    ({ variant, message }) => {
      setYardBanner({ variant, message });
      setSelectedYardItem(null);
      setPhaseTarget(null);
      queryClient.invalidateQueries({ queryKey: ['warehouse', 'yard-items'] });
      queryClient.invalidateQueries({ queryKey: ['warehouse', 'transfer-pending'] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'bobins-state-3d'] });
      setTimeout(() => setYardBanner(null), 6000);
    },
    [queryClient]
  );

  // The matching drum reports its position → fly to it (pulled back to frame the
  // whole group when `wide`), then open its group panel straight into planning a
  // beat later so the beacon is visible first. One-shot.
  const handleFocusLocated = useCallback(
    ({ pos, group, wide }) => {
      if (focusLocatedRef.current) return;
      focusLocatedRef.current = true;
      const [x, , z] = pos;
      const off = wide ? 8.5 : 3.5;
      const h = wide ? 9 : 4.2;
      flyTo({ pos: [x + off, h, z + off], look: [x, 1.1, z] });
      if (group)
        setTimeout(() => {
          setBobinAutoPlan(true);
          setSelectedBobinGroup(group);
        }, 1100);
    },
    [flyTo]
  );

  // ── Click on a building ──────────────────────────────────
  const handleBuildingClick = useCallback(
    (buildingId, clickPoint) => {
      if (viewMode === 'interior') return;

      const views = BUILDING_VIEWS[buildingId];
      if (!views) return;

      if (selectedBuilding === buildingId && viewMode === 'focus') {
        // Second click → enter interior
        let section = DEFAULT_SECTION[buildingId];

        // For right building, detect which section was clicked by Z position
        if (buildingId === 'hall-right' && clickPoint) {
          if (clickPoint.z < 41) section = 'hall-3';
          else if (clickPoint.z < 47.3) section = 'entrance';
          else section = 'hall-4';
        }

        setViewMode('interior');
        setInteriorSection(section);
        flyTo(INTERIOR_VIEWS[section]);
        setActivePreset(null);
      } else {
        // First click → focus
        focusStartRef.current = Date.now();
        setSelectedBuilding(buildingId);
        setViewMode('focus');
        setInteriorSection(null);
        flyTo(views.focus);
        setActivePreset(null);
      }
    },
    [selectedBuilding, viewMode, flyTo]
  );

  // ── Click empty space ────────────────────────────────────
  const handleMissed = useCallback(() => {
    if (viewMode === 'interior') return; // NO exit via empty space when inside
    if (viewMode === 'focus') {
      // Ignore the empty click that lands next to a building right after
      // focusing — it's almost always a near-miss of the "enter" click during
      // the fly-in, not an intentional deselect. Bounce back only later.
      if (Date.now() - focusStartRef.current < 1200) return;
      setSelectedBuilding(null);
      setViewMode('overview');
      setInteriorSection(null);
      flyTo(PRESETS.perspective);
      setActivePreset('perspective');
    }
  }, [viewMode, flyTo]);

  // ── Door portal click ────────────────────────────────────
  const handleDoorClick = useCallback(
    action => {
      if (action === 'exit') {
        setSelectedBuilding(null);
        setViewMode('overview');
        setInteriorSection(null);
        flyTo(PRESETS.perspective);
        setActivePreset('perspective');
      } else {
        // Navigate to another section
        setInteriorSection(action);
        flyTo(INTERIOR_VIEWS[action]);
      }
    },
    [flyTo]
  );

  // ── Camera presets ───────────────────────────────────────
  const handlePreset = useCallback(
    key => {
      setSelectedBuilding(null);
      setViewMode('overview');
      setInteriorSection(null);
      setActivePreset(key);
      flyTo(PRESETS[key]);
    },
    [flyTo]
  );

  const info = selectedBuilding ? BUILDING_INFO[selectedBuilding] : null;
  const isInterior = viewMode === 'interior';
  const sectionName = interiorSection ? SECTION_NAMES[interiorSection] : '';

  // Count running machines for status bar.
  // `isRunning` is now gated on feed freshness (see useMachineData), so a dead
  // link no longer counts as LIVE. Frozen ones are surfaced separately rather
  // than just vanishing from the total — disappearing quietly is how this went
  // unnoticed for two days.
  const runningCount = Object.values(machineData).filter(m => m.isRunning).length;
  const staleCount = Object.values(machineData).filter(m => m.feedStale).length;
  const totalCount = Object.keys(machineData).length;

  return (
    <div
      id="factory3d-container"
      style={{
        height: isFullscreen ? '100vh' : 'calc(100vh - 80px)',
        position: 'relative',
        background: '#1a1a2e',
        borderRadius: isFullscreen ? 0 : 8,
        overflow: 'hidden'
      }}
    >
      <Canvas
        shadows
        camera={{ position: [-35, 45, -20], fov: 55 }}
        gl={{ antialias: true, alpha: false }}
        onPointerMissed={handleMissed}
      >
        <FactoryScene
          selectedBuilding={selectedBuilding}
          onSelectBuilding={handleBuildingClick}
          viewMode={viewMode}
          interiorSection={interiorSection}
          onDoorClick={handleDoorClick}
          cameraTarget={cameraTarget}
          cameraTrigger={cameraTrigger}
          machineData={machineData}
          onOpenPanel={setOpenPanel}
          panelOpen={!!openPanel || !!selectedBobinGroup}
          onSelectBobinGroup={handleSelectBobinGroup}
          onSelectYardItem={
            // A second click while the yard panel is open would land behind it
            // and swap the drum under the user. Not folded into `panelOpen` —
            // that also hides the hall bobins, which are unrelated.
            selectedYardItem || phaseTarget ? undefined : handleSelectYardItem
          }
          focusToken={focusToken}
          onFocusLocated={handleFocusLocated}
        />
      </Canvas>

      {/* ── Deep-link fallback note (token not on the floor) ── */}
      {focusNote && (
        <div
          style={{
            position: 'absolute', top: 64, left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(245,158,11,0.96)', color: '#241a03',
            padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            maxWidth: 540, display: 'flex', alignItems: 'center', gap: 10,
            zIndex: 60, boxShadow: '0 4px 18px rgba(0,0,0,0.35)'
          }}
        >
          <FeatherIcon icon="info" size={16} />
          <span>{focusNote}</span>
          <span
            onClick={() => setFocusNote(null)}
            style={{ cursor: 'pointer', fontWeight: 800, marginLeft: 4 }}
          >
            ✕
          </span>
        </div>
      )}

      {/* ── Title ────────────────────────────────────────── */}
      <div
        style={{
          position: 'absolute', top: 12, left: 12,
          background: 'rgba(20,20,35,0.9)', color: 'white',
          padding: '8px 16px', borderRadius: 8,
          backdropFilter: 'blur(10px)',
          borderLeft: `3px solid ${isInterior && info ? info.color : '#5B9BD5'}`,
          display: 'flex', alignItems: 'flex-start', gap: 10
        }}
      >
        <div style={{ flex: 1 }}>
        {isInterior ? (
          <>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Inside {sectionName}</div>
            <div style={{ fontSize: 11, opacity: 0.7 }}>
              Use the golden doors to navigate
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              Factory 3D Layout
              {totalCount > 0 && (
                <span style={{ fontSize: 10, background: runningCount > 0 ? '#22C55E30' : '#F59E0B30', color: runningCount > 0 ? '#22C55E' : '#F59E0B', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
                  {runningCount}/{totalCount} LIVE
                </span>
              )}
              {staleCount > 0 && (
                <span
                  title="These machines are still sending their last known values. The numbers on screen are not current."
                  style={{ fontSize: 10, background: '#EF444430', color: '#F87171', padding: '2px 8px', borderRadius: 10, fontWeight: 600, cursor: 'help' }}>
                  {staleCount} FEED DEAD
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, opacity: 0.7 }}>
              {viewMode === 'focus' ? 'Click building again to enter' : 'Click a building to navigate'}
            </div>
          </>
        )}
        </div>
        <div onClick={toggleFullscreen} style={{ cursor: 'pointer', padding: 2, opacity: 0.7 }}>
          <FeatherIcon icon={isFullscreen ? 'minimize-2' : 'maximize-2'} size={16} />
        </div>
      </div>

      {/* ── Camera Controls (hidden when inside) ─────────── */}
      {!isInterior && (
        <div style={{ position: 'absolute', top: 12, right: 12 }}>
          <Card style={{ background: 'rgba(20,20,35,0.9)', border: 'none', backdropFilter: 'blur(10px)' }}>
            <Card.Body style={{ padding: '8px 10px' }}>
              <div style={{ color: '#999', fontSize: 10, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>
                Camera
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {PRESET_BUTTONS.map(p => (
                  <Button
                    key={p.key}
                    size="sm"
                    variant={activePreset === p.key ? 'primary' : 'outline-secondary'}
                    onClick={() => handlePreset(p.key)}
                    style={{ fontSize: 11, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <FeatherIcon icon={p.icon} size={12} />
                    {p.label}
                  </Button>
                ))}
              </div>
            </Card.Body>
          </Card>
        </div>
      )}

      {/* ── Building Info (focus mode only) ───────────────── */}
      {info && viewMode === 'focus' && (
        <div
          style={{
            position: 'absolute', bottom: 12, left: 12,
            background: 'rgba(20,20,35,0.95)', color: 'white',
            padding: 16, borderRadius: 8, minWidth: 240,
            borderLeft: `3px solid ${info.color}`,
            backdropFilter: 'blur(10px)'
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{info.name}</div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>{info.dims} | Height: 8m</div>
          <div style={{ fontSize: 11, opacity: 0.5, marginTop: 6 }}>Click the building to enter</div>
        </div>
      )}

      {/* ── Interior HUD ─────────────────────────────────── */}
      {isInterior && (
        <div
          style={{
            position: 'absolute', bottom: 12, left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(20,20,35,0.9)', color: 'white',
            padding: '8px 20px', borderRadius: 20, fontSize: 12,
            backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', gap: 12
          }}
        >
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: info?.color || '#fff' }} />
          <span>
            <strong>{sectionName}</strong> — WASD to walk, mouse to look, golden doors to exit
          </span>
        </div>
      )}

      {/* ── Legend (overview only) ────────────────────────── */}
      {viewMode === 'overview' && (
        <div
          style={{
            position: 'absolute', bottom: 12, right: 12,
            background: 'rgba(20,20,35,0.9)', color: 'white',
            padding: '10px 14px', borderRadius: 8, fontSize: 11,
            backdropFilter: 'blur(10px)'
          }}
        >
          <div style={{ color: '#999', fontSize: 10, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>
            Buildings
          </div>
          <LegendRow label="Hall 1" dims="82.7m x 11.5m" color="#5B9BD5" onClick={() => handleBuildingClick('hall-1')} />
          <LegendRow label="Hall 2" dims="26m x 11.5m" color="#70AD47" onClick={() => handleBuildingClick('hall-2')} />
          <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0', cursor: 'pointer' }}
              onClick={() => handleBuildingClick('hall-right')}
            >
              <div style={{ display: 'flex', gap: 1 }}>
                <div style={{ width: 4, height: 12, borderRadius: '2px 0 0 2px', background: '#ED7D31' }} />
                <div style={{ width: 4, height: 12, background: '#9B59B6' }} />
                <div style={{ width: 4, height: 12, borderRadius: '0 2px 2px 0', background: '#FFC000' }} />
              </div>
              <span>Hall 3 + 4</span>
              <span style={{ opacity: 0.5, marginLeft: 'auto' }}>82.3m x 11.6m</span>
            </div>
          </div>

          <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ color: '#999', fontSize: 9, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 1 }}>
              Warehouse
            </div>
            <LegendRow label="Deposit 1" dims="33m x 37m" color="#E67E22" onClick={() => handleBuildingClick('deposit-1')} />
            <LegendRow label="Deposit 2" dims="32m x 13m" color="#8E44AD" onClick={() => handleBuildingClick('deposit-2')} />
          </div>

          <div style={{ marginTop: 8, opacity: 0.4, fontSize: 10, lineHeight: 1.5 }}>
            Click building to focus<br />
            Click again to enter<br />
            Scroll to zoom | Drag to orbit
          </div>
        </div>
      )}

      {/* Machine Panel overlay */}
      {openPanel && (
        <MachinePanel
          machineId={openPanel}
          data={machineData[openPanel]}
          onClose={() => setOpenPanel(null)}
        />
      )}

      {/* Bobin Group Detail Panel */}
      {selectedBobinGroup && (
        <BobinGroupPanel
          group={selectedBobinGroup}
          autoPlan={bobinAutoPlan}
          onClose={() => {
            setSelectedBobinGroup(null);
            setBobinAutoPlan(false);
          }}
        />
      )}

      {/* ── Yard drum panel ──────────────────────────────── */}
      {selectedYardItem && (
        <YardItemPanel
          item={selectedYardItem}
          onAddPhase={() => setPhaseTarget(yardPhaseTarget(selectedYardItem))}
          onClose={() => setSelectedYardItem(null)}
        />
      )}

      {/* Shared with the Production → Warehouse table: same endpoint, same
          wording. Rendered outside the Canvas — a react-bootstrap Modal is DOM,
          and R3F's reconciler would refuse it inside the scene. */}
      <AddPhaseDialog
        target={phaseTarget}
        onHide={() => setPhaseTarget(null)}
        onDone={handleYardPhaseAdded}
      />

      {/* ── Result of an Add-phase done from the yard ─────── */}
      {yardBanner && (
        <div
          style={{
            position: 'absolute', top: 64, left: '50%',
            transform: 'translateX(-50%)',
            background:
              yardBanner.variant === 'warning'
                ? 'rgba(245,158,11,0.96)'
                : 'rgba(34,197,94,0.96)',
            color: yardBanner.variant === 'warning' ? '#241a03' : '#04220f',
            padding: '8px 14px', borderRadius: 8,
            fontSize: 12, fontWeight: 600, maxWidth: 520,
            boxShadow: '0 4px 20px rgba(0,0,0,0.4)', zIndex: 120
          }}
        >
          {yardBanner.message}
        </div>
      )}
    </div>
  );
}

// ─── YARD DRUM PANEL ───────────────────────────────────────
//
// What was clicked, and the one action that belongs to a finished drum standing
// in the yard: it is not finished after all. Everything else about the drum
// (putting it away, moving it, shipping it) is warehouse work and lives in the
// warehouse screens; this is the production door back.
function YardItemPanel({ item, onAddPhase, onClose }) {
  const t = item.traceability || {};
  const isPallet = item.type === 'europallet';
  const canAddPhase = !!t.jobId && !isPallet;

  const row = (label, value) =>
    value == null || value === '' ? null : (
      <div style={{ display: 'flex', gap: 8, fontSize: 11, padding: '2px 0' }}>
        <span style={{ opacity: 0.55, minWidth: 74 }}>{label}</span>
        <span style={{ fontWeight: 600 }}>{value}</span>
      </div>
    );

  return (
    <div
      style={{
        position: 'absolute', top: 64, right: 12,
        background: 'rgba(20,20,35,0.96)', color: 'white',
        padding: 14, borderRadius: 10, width: 280,
        borderLeft: '3px solid #F59E0B',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 8px 30px rgba(0,0,0,0.5)', zIndex: 110
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{item.cableName}</div>
          <div style={{ fontSize: 10, opacity: 0.55, fontFamily: 'monospace' }}>
            {item.serial}
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none', border: 'none', color: '#999',
            fontSize: 18, lineHeight: 1, cursor: 'pointer', padding: 0
          }}
          aria-label="close"
        >
          ×
        </button>
      </div>

      <div style={{ marginTop: 10, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 8 }}>
        {row('Type', isPallet ? 'Europallet' : 'Drum')}
        {row('Colour', t.color)}
        {row('Length', `${Number(item.lengthMeters || 0).toLocaleString()} m`)}
        {row('Weight', item.weightKg ? `${Number(item.weightKg).toLocaleString()} kg` : null)}
        {row('Finished at', t.phase ? String(t.phase).replaceAll('_', ' ') : null)}
        {row('For', item.customerName || 'STOCK')}
        {row('Job', t.jobCode || t.jobId)}
      </div>

      <Button
        variant={canAddPhase ? 'warning' : 'secondary'}
        size="sm"
        className="w-100 mt-3 fw-bold"
        disabled={!canAddPhase}
        onClick={onAddPhase}
        title={
          isPallet
            ? 'A pallet holds coils from several jobs — unpack it at the Packing Station'
            : undefined
        }
      >
        {isPallet
          ? 'Pallet — unpack at Packing Station'
          : canAddPhase
            ? 'Add phase…'
            : 'No job attached'}
      </Button>

      {canAddPhase && (
        <div style={{ fontSize: 10, opacity: 0.45, marginTop: 6, lineHeight: 1.4 }}>
          Sends it back to production — the job reopens and the drum leaves the
          yard.
        </div>
      )}
    </div>
  );
}

/** StorageItem → the shape AddPhaseDialog reads. */
function yardPhaseTarget(item) {
  if (!item) return null;
  const t = item.traceability || {};
  return {
    jobId: t.jobId,
    tokenId: t.tokenId,
    phase: t.phase,
    cableName: item.cableName,
    displayId: item.serial,
    lengthMeters: item.lengthMeters,
    locationKind: item.location?.kind
  };
}

function LegendRow({ label, dims, color, onClick }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0', cursor: 'pointer' }} onClick={onClick}>
      <div style={{ width: 12, height: 12, borderRadius: 2, background: color, flexShrink: 0 }} />
      <span>{label}</span>
      <span style={{ opacity: 0.5, marginLeft: 'auto' }}>{dims}</span>
    </div>
  );
}
